<template>
  <v-container fluid class="pt-0">
    <v-row v-if="!showShare || !is_admin">
      <v-col cols="12" class="pa-0">
        <v-card color="white" tile>
          <v-container class="px-0">
            <v-row class="overflow-hidden mx-0">
              <v-col cols="auto" class="pa-0">
                <v-card-title>
                  Hello, {{ profile.firstName }} &#128075;
                </v-card-title>
                <v-card-subtitle>
                  <template v-if="settings.isPaidPlan">
                    Here's what's happening in CoreCounts
                  </template>
                  <template v-else>
                    We're excited to have you here with us today!
                  </template>
                </v-card-subtitle>
              </v-col>
              <v-spacer> </v-spacer>
              <v-col cols="auto" v-if="$vuetify.breakpoint.mdAndUp">
                <v-img width="300" contain :src="$images.CC_BACKGROUND"></v-img>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
    </v-row>
    <v-container>
      <loading-spinner v-if="loading"></loading-spinner>
      <template v-else-if="!settings.isPaidPlan && is_admin">
        <v-row v-if="showShare">
          <v-col cols="12" md="8">
            <v-row>
              <v-col cols="12">
                <dashboard-share-card
                  v-on:refresh="refreshUsers"
                ></dashboard-share-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="4">
            <v-row>
              <v-col cols="12">
                <dashboard-share-instructions-faq-card
                  v-on:refresh="refreshUsers"
                >
                </dashboard-share-instructions-faq-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <ad-header-card
              class="border--dashed border--2"
              title="Great, we're starting to collect data now! 😎"
              :subtitle="
                `You've added ${users.length -
                  1} people to your account so far. <span class='text-decoration-underline'>Scroll down to view your eNPS score.</span>`
              "
            ></ad-header-card>
          </v-col>
          <v-col cols="12" md="4">
            <dashboard-accepted-invitation-card></dashboard-accepted-invitation-card>
          </v-col>
          <v-col cols="12" md="8">
            <dashboard-setup-culture-tracking-card></dashboard-setup-culture-tracking-card>
          </v-col>
          <v-col cols="12">
            <core-board
              :company="{ name: this.profile.companyName }"
              :sub-title="
                `Limited data collected from ${users.length - 1} people`
              "
              hide-filter
            ></core-board>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row class="pb-4" v-if="pulse_sent == 0 && is_admin">
          <v-col cols="12" sm="6" md="4">
            <dashboard-accepted-invitation-card></dashboard-accepted-invitation-card>
          </v-col>
          <v-col cols="12" sm="6" md="8">
            <dashboard-setup-culture-tracking-card
              v-if="
                is_admin &&
                  settings.featureIds.filter(x => x != $enums.features.PULSE) ==
                    0 &&
                  (settings.coreValues.length == 0 ||
                    !settings.missionStatement ||
                    !pulse_scheduled) &&
                  pulse_sent == 0
              "
            ></dashboard-setup-culture-tracking-card>
          </v-col>
        </v-row>
        <template v-if="settings.isRecognitionActive">
          <v-row class="pt-4">
            <v-col cols="auto" class="pr-0">
              <v-avatar size="30" class="elevation-4">
                <v-img :src="$images.RECOGNITION_AVATAR"></v-img>
              </v-avatar>
            </v-col>
            <v-col cols="auto" class="text-h5 font-weight-bold secondary--text">
              Recognition Feed
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <router-link to="/features/recognition" class="info--text">
                View All
                <v-icon color="info" small>
                  keyboard_arrow_right
                </v-icon>
              </router-link>
            </v-col>
          </v-row>
          <recognition-feed></recognition-feed>
        </template>
        <template
          v-if="settings.coreCardTypes && settings.coreCardTypes.length > 0"
        >
          <dashboard-core-board></dashboard-core-board>
        </template>
      </template>
    </v-container>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import DashboardCoreBoard from "@/components/dashboard/DashboardCoreBoard";
import RecognitionFeed from "@/components/recognition/RecognitionFeed";
import AdHeaderCard from "@/components/common/AdHeaderCard";
import DashboardShareCard from "@/components/dashboard/DashboardShareCard";
import DashboardShareInstructionsFaqCard from "@/components/dashboard/DashboardShareInstructionsFaqCard";
import DashboardAcceptedInvitationCard from "@/components/dashboard/DashboardAcceptedInvitationCard";
import DashboardSetupCultureTrackingCard from "@/components/dashboard/DashboardSetupCultureTrackingCard";
import CoreBoard from "@/components/corecard/CoreBoard";
export default {
  name: "Dashboard",
  components: {
    DashboardCoreBoard,
    RecognitionFeed,
    AdHeaderCard,
    DashboardShareCard,
    DashboardShareInstructionsFaqCard,
    DashboardAcceptedInvitationCard,
    DashboardSetupCultureTrackingCard,
    CoreBoard
  },
  data: () => ({
    loading: false
  }),
  computed: {
    isAdminOrManager() {
      return this.is_admin || this.is_manager;
    },
    showShare() {
      return this.users && this.users.length < 4 && !this.settings.isPaidPlan;
    },
    ...mapGetters({
      profile: "profile/profile",
      tasks: "task/tasks",
      users: "user/users",
      surveys: "survey/surveys",
      settings: "profile/settings",
      is_admin: "profile/is_admin",
      is_manager: "profile/is_manager",
      pulse_scheduled: "pulse/pulse_scheduled",
      pulse_sent: "pulse/pulse_sent"
    })
  },
  mounted() {
    this.loading = true;
    let calls = [
      this.$store.dispatch("task/getTasks"),
      this.$store.dispatch("task/getAlerts")
    ];
    if (this.isAdminOrManager) {
      calls.push(this.$store.dispatch("company/getCoreValues"));
      calls.push(this.$store.dispatch("user/getUsers"));
      if (!this.settings.isPaidPlan) {
        calls.push(this.$store.dispatch("pulse/getOverview"));
      }
    }
    Promise.all(calls)
      .catch(error => {
        this.logError(error);
        this.$root.$alert(
          "Error Occurred",
          "An error occured while loading dashboard."
        );
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    refreshUsers() {
      this.loading = true;
      this.$store
        .dispatch("user/getUsers")
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading dashboard."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>
